import {
  BillingTerm,
  MAX_TEAM_SEATS,
  TEAM_CHECKOUT_SUCCESS_PATH,
  TEAM_MIN_SEATS,
  TeamCheckoutSource,
  paddleTeamItemsNewAnnualProd,
  paddleTeamItemsNewAnnualSandbox,
  paddleTeamItemsNewMonthlyProd,
  paddleTeamItemsNewMonthlySandbox,
} from '@easy-expense/utils-shared';

import { SETTINGS_BILLING_CHECKOUT_COMPLETE_ROUTE, SETTINGS_BILLING_ROUTE } from './constants';

export type TeamCheckoutRequest = {
  userID: string;
  /** Requested seat count, already floored to the Seat Minimum. */
  seats: number;
  term: BillingTerm;
  email: string;
  source: TeamCheckoutSource | null;
  /** Scopes the billing lookup behind Trial Eligibility and the Purchase Floor; web sources only. */
  workspaceID: string | null;
};

/** Where the buyer goes when checkout completes, and when they back out of it. */
export type TeamCheckoutDestinations = {
  success: string;
  /** Null where there is nothing to go back to -- a tab the mobile app opened fresh. */
  cancel: string | null;
};

const BILLING_TERMS: BillingTerm[] = ['monthly', 'annual'];
const CHECKOUT_SOURCES: TeamCheckoutSource[] = [
  'mobile_paywall',
  'mobile_seat_banner',
  'mobile_invite',
  'web_onboarding',
  'web_billing',
];

/**
 * One record per Checkout Source, so a source decides both how the purchase is attributed and
 * where its buyer ends up. Kept in ee-web rather than the shared lib deliberately: these are web
 * route names, and a mobile flow must never redirect into one of them -- landing behind a login
 * wall is the thing the browser handoff exists to avoid.
 */
const DESTINATIONS: Record<TeamCheckoutSource, TeamCheckoutDestinations> = {
  mobile_paywall: { success: TEAM_CHECKOUT_SUCCESS_PATH, cancel: null },
  mobile_seat_banner: { success: TEAM_CHECKOUT_SUCCESS_PATH, cancel: null },
  mobile_invite: { success: TEAM_CHECKOUT_SUCCESS_PATH, cancel: null },
  // Back goes to the setup page the checkout was opened from, not to `/teams/onboarding`: that
  // route is the signup screen, which bounces a signed-in buyer straight back here anyway, but
  // flashes a sign-up form and re-fires `team_onboarding_started` on the way through.
  web_onboarding: { success: '/teams/invite', cancel: '/teams/pay/TEAMS_BASIC' },
  web_billing: {
    success: SETTINGS_BILLING_CHECKOUT_COMPLETE_ROUTE,
    cancel: SETTINGS_BILLING_ROUTE,
  },
};

/**
 * Reads a `/checkout/team` request off its query string (see buildTeamCheckoutQuery in
 * utils-shared). Returns null when the request cannot bind a purchase to a user -- a checkout
 * without a userID would create a Paddle subscription the webhook can only attach by email
 * lookup, which is the fallback path, not the contract. A missing or unknown `term` falls back
 * to monthly and an unparseable `seats` to the Seat Minimum; the URL never carries a price ID or
 * plan code, so nothing here can select a deprecated or legacy price.
 */
export function parseTeamCheckoutRequest(params: URLSearchParams): TeamCheckoutRequest | null {
  const userID = params.get('userID');
  if (!userID) {
    return null;
  }
  const termParam = params.get('term') as BillingTerm | null;
  const term = termParam && BILLING_TERMS.includes(termParam) ? termParam : 'monthly';
  const requestedSeats = Number(params.get('seats'));
  const seats = Number.isFinite(requestedSeats)
    ? Math.max(Math.floor(requestedSeats), TEAM_MIN_SEATS)
    : TEAM_MIN_SEATS;
  const sourceParam = params.get('source') as TeamCheckoutSource | null;
  const source = sourceParam && CHECKOUT_SOURCES.includes(sourceParam) ? sourceParam : null;

  return {
    userID,
    seats,
    term,
    email: params.get('email') ?? '',
    source,
    workspaceID: params.get('workspaceID'),
  };
}

/**
 * Whether this checkout was opened from inside ee-web, and so runs with a signed-in session
 * behind it. Web requests resolve their price and seat floor from the buyer's real billing state;
 * mobile requests have no session to ask and keep the defaults.
 */
export function isWebCheckoutSource(source: TeamCheckoutSource | null): boolean {
  return source === 'web_onboarding' || source === 'web_billing';
}

/** Where this checkout returns its buyer. An unrecognised source is treated as a handoff. */
export function teamCheckoutDestinations(
  source: TeamCheckoutSource | null,
): TeamCheckoutDestinations {
  if (!source) {
    return { success: TEAM_CHECKOUT_SUCCESS_PATH, cancel: null };
  }
  return DESTINATIONS[source];
}

/**
 * Purchase Floor: the fewest seats this workspace may buy -- the Seat Minimum, or its active
 * member count if that is higher, so a purchase can never leave existing members uncovered.
 * Falls back to the Seat Minimum alone when the member count is unknown, which is every checkout
 * opened without a session.
 *
 * Held to the Seat Maximum at the top, because the Floor is a bound on the same stepper the cap
 * bounds and the two cannot be allowed to cross. Unclamped, a workspace with more members than
 * MAX_TEAM_SEATS jammed the checkout outright: the seat effect opened it above the cap, both
 * stepper buttons disabled at once (at the floor and at the cap), typing anything snapped to
 * MAX_TEAM_SEATS -- below the floor the page claims to enforce -- and Paddle was handed the
 * unclamped quantity, selling past a ceiling that exists to stop exactly that.
 *
 * The clamp is the lesser of two wrongs, not a fix for such a workspace: a team above the Seat
 * Maximum genuinely cannot cover every member self-serve, and covering as many as the cap allows
 * beats a checkout that cannot be completed at all. Routing them to a sales conversation is what
 * MAX_TEAM_SEATS's own contract calls for, and is still owed.
 */
export function teamPurchaseFloor(activeSeats: number | null): number {
  if (activeSeats === null || !Number.isFinite(activeSeats)) {
    return TEAM_MIN_SEATS;
  }
  return Math.min(Math.max(TEAM_MIN_SEATS, Math.floor(activeSeats)), MAX_TEAM_SEATS);
}

/**
 * The one price a Team checkout may sell: the current TEAMS_BASIC variant for the requested term.
 * Prod/sandbox is decided by the build, never by hostname.
 *
 * `trialEligible` is the caller's answer to Trial Eligibility, never the URL's -- a buyer who has
 * subscribed before does not earn a second trial by cancelling and coming back. Checkouts opened
 * without a session cannot ask, and keep the trial variant the browser handoff has always sold.
 */
export function resolveTeamCheckoutPriceID(
  term: BillingTerm,
  isProduction: boolean,
  trialEligible: boolean,
): string {
  if (term === 'annual') {
    const annualItems = isProduction
      ? paddleTeamItemsNewAnnualProd
      : paddleTeamItemsNewAnnualSandbox;
    return trialEligible ? annualItems.TEAMS_BASIC.trial : annualItems.TEAMS_BASIC.noTrial;
  }
  const monthlyItems = isProduction
    ? paddleTeamItemsNewMonthlyProd
    : paddleTeamItemsNewMonthlySandbox;
  return trialEligible ? monthlyItems.TEAMS_BASIC.trial : monthlyItems.TEAMS_BASIC.noTrial;
}
